import * as Sentry from '@sentry/browser'
import { html, css } from 'lit'

// For form accessible components
import 'element-internals-polyfill'

import '../shared/components/app-header.js'
import './ui/shared/toast-alert.js'
import '../shared/router/mobile-nav-bar.js'

import '../shared/assets/fonts/Lato-Bold.ttf'
import './data/services/users.service.js'
import '../shared/assets/fonts/Lato-Regular.ttf'

import Globals from '../shared/Globals.js'

import { getSessionGlobals } from './utilities/session.js'
import { getSessionUser } from './utilities/session.js'
import './data/services/users.service.js'

import ToastAlertElement from './ui/shared/toast-alert.js'
import AppEvents from './utilities/app-events.js'
import RouterMixin, { go } from '../shared/router/router-mixin.js'
import Logger from '../shared/Logger.js'
import { refresh, checkValidToken } from './data/services/users.service.js'
import routes from '../shared/router/routes.js'

if (process.env.SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.SENTRY_DSN,
    environment: process.env.ENVIRONMENT,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [new RegExp(process.env.APP_URL + '.*')],
    // Session Replay
    replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  })

  window.Logger = new Logger({
    Sentry,
    app: 'kula-client',
  })
} else {
  console.warn(
    'Sentry DSN not found. Sentry is currently disabled. Please provide the appropriate environment variable.'
  )
}

// Initialize globals
window.Globals = new Globals(getSessionGlobals())

class AppEnterElement extends RouterMixin {
  constructor() {
    super()
    window.addEventListener(AppEvents.TOAST, this.fireToast_.bind(this))
  }

  connectedCallback() {
    super.connectedCallback()
    this.intervalRefreshToken()
    this.checkToken()
  }

  handleUpdate() {
    if (
      window.location.hostname !== 'localhost' &&
      window.location.hostname !== '127.0.0.1'
    ) {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker
          .getRegistration()
          .then((registration) => {
            if (registration) {
              registration.update()
            } else {
              console.error('Service worker registration not found.')
            }
          })
          .catch((error) => {
            console.error(
              `Error checking for service worker registration: ${error}`
            )
          })
      }
    }
  }

  render() {
    this.handleUpdate()

    return html`
      ${this.showHeader
        ? html`<app-header
            .viewHasToolbar=${this.hasToolbar}
            .hideWalletSelect=${this.hideWalletSelect}
          ></app-header>`
        : ''}
      <slot ?showMobileNav=${this.showMobileNav} ?showHeader=${this.showHeader}>
      </slot>
      <toast-alert></toast-alert>
      ${this.showMobileNav ? html`<mobile-nav-bar></mobile-nav-bar>` : ''}
    `
  }

  fireToast_({ detail: { text } }) {
    const toast = /** @type {ToastAlertElement} */ (
      this.shadowRoot.querySelector('toast-alert')
    )

    toast.show(text, 2500)
  }

  async intervalRefreshToken() {
    setInterval(async () => {
      try {
        await refresh()
      } catch (error) {
        console.error('error refreshing token', error)
      }
    }, 1000 * 60 * 5)
  }

  async checkToken() {
    try {
      const response = await checkValidToken()
      if (response) {
        return
      } else {
        const queryParams = new URLSearchParams(window.location.search)
        const params = Array.from(queryParams.entries()).reduce(
          (acc, [key, value]) => {
            acc[key] = value
            return acc
          },
          {}
        )
        go(routes.LOGIN.path, Object.keys(params).length > 0 ? params : null)
      }
    } catch (error) {
      console.error('error checking token', error)
    }
  }

  static styles = css`
    app-header {
      position: fixed;
      top: 0px;
      height: var(--header-height, 50px);
      z-index: 5;
    }
    mobile-nav-bar {
      position: fixed;
      bottom: 0px;
      z-index: 4;
    }
    toast-alert {
      z-index: 9999;
    }
    slot {
      display: block;
    }
    slot[showHeader] {
      margin-top: var(--header-height, 50px);
    }
    slot[showMobileNav] {
      margin-bottom: var(--mobile-nav-height, 70px);
    }
  `
}

customElements.define('app-enter', AppEnterElement)
export default AppEnterElement
